import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button, LiveIndicator } from '@/components/common';
import {
  LayoutDashboard,
  Server,
  Activity,
  Network,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
  User,
  Globe,
  BookOpen,
  Sparkles,
  Power,
  Users,
  UserCog,
  ScrollText,
  FolderOpen,
  Trash2,
  BarChart3,
  Star,
  ShieldCheck,
  ChevronDown,
} from 'lucide-react';

import type { Role } from '@/contexts/AuthContext';
import { prefixFor } from '@/lib/rolePath';

type GroupKey = 'admin';

interface NavItem {
  /** Relative path (no role prefix). Layout prepends '/<prefix>'. */
  relPath: string;
  label: string;
  icon: React.ReactNode;
  /** Explicit set of roles allowed to see this nav item. Drives the
   *  sidebar visibility matrix (DR + Auth Enhancements P6, 2026-05-24). */
  roles: Role[];
  /** When set, this item is nested under the matching collapsible
   *  section header (see NAV_GROUPS) instead of sitting at top level. */
  group?: GroupKey;
}

/** Collapsible main-sidebar sections. An item joins one via `group`. */
const NAV_GROUPS: Record<GroupKey, { label: string; icon: React.ReactNode }> = {
  admin: { label: 'Administration', icon: <ShieldCheck size={20} /> },
};

const ALL: Role[] = ['admin', 'partner', 'user'];
const ADMIN_ONLY: Role[] = ['admin'];
const ADMIN_AND_PARTNER: Role[] = ['admin', 'partner'];

const navItems: NavItem[] = [
  { relPath: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} />, roles: ALL },
  { relPath: '/nodes', label: 'Nodes', icon: <Server size={20} />, roles: ADMIN_ONLY },
  { relPath: '/lifecycle', label: 'Lifecycle', icon: <Power size={20} />, roles: ADMIN_ONLY },
  { relPath: '/monitoring', label: 'Monitoring', icon: <Activity size={20} />, roles: ALL },
  { relPath: '/pulse-groups', label: 'Pulse Groups', icon: <Network size={20} />, roles: ALL },
  { relPath: '/wiki', label: 'Documentation', icon: <BookOpen size={20} />, roles: ADMIN_ONLY },
  { relPath: '/tech-docs', label: 'Tech Documentation', icon: <Sparkles size={20} />, roles: ALL },
  { relPath: '/users', label: 'Users', icon: <Users size={20} />, roles: ADMIN_AND_PARTNER },
  { relPath: '/data-room', label: 'Data Room', icon: <FolderOpen size={20} />, roles: ALL },
  { relPath: '/data-room/favorites', label: 'Favorites', icon: <Star size={20} />, roles: ALL },
  // --- Administration (collapsible) — admin-only oversight/maintenance tools ---
  { relPath: '/audit', label: 'Audit Log', icon: <ScrollText size={20} />, roles: ADMIN_ONLY, group: 'admin' },
  { relPath: '/data-room/trash', label: 'DR Trash', icon: <Trash2 size={20} />, roles: ADMIN_ONLY, group: 'admin' },
  { relPath: '/data-room/stats', label: 'DR Stats', icon: <BarChart3 size={20} />, roles: ADMIN_ONLY, group: 'admin' },
  { relPath: '/data-room/activity', label: 'DR Activity', icon: <ScrollText size={20} />, roles: ADMIN_ONLY, group: 'admin' },
  { relPath: '/profile', label: 'Profile', icon: <UserCog size={20} />, roles: ALL },
];

/** A nav item with its role-prefixed absolute path resolved. */
type VisibleNavItem = NavItem & { path: string };

/** A single sidebar link. Reused for top-level items and group children, in
 *  both the desktop sidebar and the mobile drawer. */
function NavLeaf({
  item,
  collapsed,
  onNavigate,
}: {
  item: VisibleNavItem;
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <li>
      <NavLink
        to={item.path}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
            isActive
              ? 'bg-primary-50 text-primary'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          )
        }
      >
        <span className="flex-shrink-0">{item.icon}</span>
        {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
      </NavLink>
    </li>
  );
}

/** A collapsible section header + its nested child links. When the whole
 *  sidebar is collapsed to the icon rail (w-16), the header is dropped and the
 *  children render as flat icons so every page stays reachable. */
function NavGroup({
  label,
  icon,
  items,
  collapsed,
  expanded,
  active,
  onToggle,
  onNavigate,
}: {
  label: string;
  icon: React.ReactNode;
  items: VisibleNavItem[];
  collapsed?: boolean;
  expanded: boolean;
  active: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}) {
  if (collapsed) {
    return (
      <>
        {items.map((it) => (
          <NavLeaf key={it.path} item={it} collapsed onNavigate={onNavigate} />
        ))}
      </>
    );
  }
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors w-full',
          active ? 'text-primary' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        )}
      >
        <span className="flex-shrink-0">{icon}</span>
        <span className="text-sm font-medium flex-1 text-left">{label}</span>
        {expanded ? (
          <ChevronDown size={16} className="flex-shrink-0 text-gray-400" />
        ) : (
          <ChevronRight size={16} className="flex-shrink-0 text-gray-400" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="ml-4 pl-2 border-l border-gray-100 space-y-1 mt-1 overflow-hidden"
          >
            {items.map((it) => (
              <NavLeaf key={it.path} item={it} onNavigate={onNavigate} />
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
}

export const AdminLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const currentRole: Role = (user?.role ?? 'user') as Role;
  const prefix = prefixFor(currentRole);
  // Expand each visible nav item with the role-prefixed absolute path.
  const visibleNavItems = navItems
    .filter((it) => it.roles.includes(currentRole))
    .map((it) => ({ ...it, path: `/${prefix}${it.relPath}` }));
  const dashboardPath = `/${prefix}/dashboard`;
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get current page title.
  // Longest-prefix-match (not exact-match) so nested routes like
  // /admin/data-room/folders/:id resolve to the "Data Room" nav item label
  // instead of falling back to "Admin". Sort by path length descending so
  // more-specific matches (e.g., /admin/data-room/trash → "DR Trash") win
  // over shorter prefixes that also match. The `+ '/'` in the startsWith
  // check prevents false positives like /admin/users-experimental matching
  // /admin/users.
  const currentPage = [...visibleNavItems]
    .sort((a, b) => b.path.length - a.path.length)
    .find(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + '/')
    );
  const pageTitle = currentPage?.label || 'Admin';

  // --- Collapsible nav groups (e.g. "Administration") ----------------------
  // Which group, if any, contains the active route — used to auto-expand it.
  const activeGroupKey =
    (Object.keys(NAV_GROUPS) as GroupKey[]).find((key) =>
      visibleNavItems.some(
        (it) =>
          it.group === key &&
          (location.pathname === it.path ||
            location.pathname.startsWith(it.path + '/'))
      )
    ) ?? null;

  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(activeGroupKey ? [activeGroupKey] : [])
  );
  // Keep the active group expanded as the route changes.
  useEffect(() => {
    if (activeGroupKey) {
      setOpenGroups((prev) =>
        prev.has(activeGroupKey) ? prev : new Set(prev).add(activeGroupKey)
      );
    }
  }, [activeGroupKey]);
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Ordered render model: top-level items in place, each group emitted once at
  // the position of its first visible member.
  type RenderEntry =
    | { kind: 'item'; item: VisibleNavItem }
    | { kind: 'group'; key: GroupKey; items: VisibleNavItem[] };
  const renderEntries: RenderEntry[] = [];
  const seenGroups = new Set<string>();
  for (const it of visibleNavItems) {
    if (!it.group) {
      renderEntries.push({ kind: 'item', item: it });
      continue;
    }
    if (seenGroups.has(it.group)) continue;
    seenGroups.add(it.group);
    renderEntries.push({
      kind: 'group',
      key: it.group,
      items: visibleNavItems.filter((x) => x.group === it.group),
    });
  }

  const renderNavList = (opts: { collapsed: boolean; onNavigate?: () => void }) => (
    <ul className="space-y-1 px-2">
      {renderEntries.map((entry) =>
        entry.kind === 'item' ? (
          <NavLeaf
            key={entry.item.path}
            item={entry.item}
            collapsed={opts.collapsed}
            onNavigate={opts.onNavigate}
          />
        ) : (
          <NavGroup
            key={entry.key}
            label={NAV_GROUPS[entry.key].label}
            icon={NAV_GROUPS[entry.key].icon}
            items={entry.items}
            collapsed={opts.collapsed}
            expanded={openGroups.has(entry.key)}
            active={activeGroupKey === entry.key}
            onToggle={() => toggleGroup(entry.key)}
            onNavigate={opts.onNavigate}
          />
        )
      )}
    </ul>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-40',
          'bg-white border-r border-gray-200',
          'transition-all duration-300',
          isSidebarCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          <Link to={dashboardPath} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Globe size={16} className="text-white" />
            </div>
            {!isSidebarCollapsed && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-lg font-bold text-gray-900"
              >
                DDARP
              </motion.span>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
          {renderNavList({ collapsed: isSidebarCollapsed })}
        </nav>

        {/* Collapse Toggle */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors shadow-sm"
        >
          {isSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        {/* User Info */}
        <div className="p-4 border-t border-gray-200">
          <div className={cn('flex items-center gap-3', isSidebarCollapsed && 'justify-center')}>
            <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
              <User size={16} className="text-primary" />
            </div>
            {!isSidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.email || 'Loading…'}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.role || 'guest'}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div
        className={cn(
          'flex-1 min-w-0 flex flex-col transition-all duration-300',
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        )}
      >
        {/* Top Header */}
        <header className="h-16 bg-white/95 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-30">
          <div className="h-full px-4 lg:px-6 flex items-center justify-between">
            {/* Left: Mobile Menu + Title */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden p-2 text-gray-400 hover:text-gray-700"
              >
                <Menu size={24} />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
              </div>
            </div>

            {/* Right: Live Indicator + Logout */}
            <div className="flex items-center gap-4">
              <LiveIndicator variant="success" text="LIVE" size="sm" />
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<LogOut size={16} />}
                onClick={handleLogout}
              >
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 min-w-0 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
            />

            {/* Mobile Sidebar */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-gray-200 z-50 lg:hidden shadow-lg"
            >
              {/* Header */}
              <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
                <Link to={dashboardPath} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                    <Globe size={16} className="text-white" />
                  </div>
                  <span className="text-lg font-bold text-gray-900">DDARP</span>
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Navigation */}
              <nav className="py-4">
                {renderNavList({
                  collapsed: false,
                  onNavigate: () => setIsMobileMenuOpen(false),
                })}
              </nav>

              {/* User Info */}
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
                    <User size={16} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.email || 'Loading…'}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">{user?.role || 'guest'}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  leftIcon={<LogOut size={16} />}
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminLayout;
